import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, type AdminUser } from "../../../api";
import { fadeUp, stagger } from "../../../lib/motion";

const PERSONAS = [
  { id: "casual",      name: "Casual",            line: "Your regular chatty friend." },
  { id: "grumpy",      name: "Grumpy Old Timer",  line: "Cynical. Blunt. Zero nonsense." },
  { id: "optimist",    name: "Cheerful Optimist", line: "Annoyingly upbeat, in the best way." },
  { id: "philosopher", name: "Deep Thinker",      line: "Questions everything." },
  { id: "gossip",      name: "Nosy Friend",       line: "Wants the story." },
  { id: "minimalist",  name: "Dry Minimalist",    line: "Says little. Means a lot." },
];

export default function Content() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const all: AdminUser[] = [];
        let cursor: string | undefined;
        for (let i = 0; i < 5; i++) {
          const res = await api.listUsers(cursor);
          all.push(...res.users);
          if (!res.next_cursor) break;
          cursor = res.next_cursor;
        }
        setUsers(all);
      } catch (e: unknown) {
        setErr(e instanceof Error ? e.message : "failed");
      }
    })();
  }, []);

  const counts: Record<string, number> = Object.fromEntries(PERSONAS.map((p) => [p.id, 0]));
  if (users) {
    for (const u of users) {
      const p = (u as unknown as { persona?: string }).persona || "casual";
      counts[p] = (counts[p] || 0) + 1;
    }
  }
  const total = users?.length || 0;
  const maxCount = Math.max(1, ...Object.values(counts));

  return (
    <motion.div initial="hidden" animate="show" variants={stagger(0.06)}>
      <motion.div variants={fadeUp}>
        <div className="font-serif italic text-ink-soft">admin · content</div>
        <h2 className="display text-4xl md:text-5xl mt-2">Personalities.</h2>
        <p className="mt-3 text-ink-soft max-w-xl">
          Six personality prompts, live in{" "}
          <code className="text-xs px-1.5 py-0.5 bg-ink-card text-paper rounded">src/config.py</code>.
          Below: how your users are distributed across them.
        </p>
      </motion.div>

      {err && <div className="mt-4 text-terracotta text-sm">{err}</div>}
      {!users && !err && <div className="mt-8 text-ink-faint">Loading…</div>}

      {users && (
        <motion.div variants={fadeUp} className="mt-8 card">
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">user distribution</div>
              <h3 className="font-serif text-2xl mt-1">Which personality wins</h3>
            </div>
            <div className="text-sm text-ink-faint">{total} users</div>
          </div>
          <div className="space-y-4">
            {PERSONAS.map((p) => {
              const c = counts[p.id] ?? 0;
              const pct = total ? (c / total) * 100 : 0;
              const barPct = (c / maxCount) * 100;
              return (
                <div key={p.id} className="grid grid-cols-[1fr_auto_auto] items-center gap-4">
                  <div className="min-w-0">
                    <div className="font-serif text-lg leading-tight">{p.name}</div>
                    <div className="text-xs text-ink-faint italic truncate">{p.line}</div>
                  </div>
                  <div className="w-40 md:w-64 h-2 bg-[rgba(42,36,26,0.08)] rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${barPct}%` }}
                      transition={{ duration: 0.9 }}
                      className="h-full bg-terracotta"
                    />
                  </div>
                  <div className="text-right text-sm tabular-nums min-w-[80px]">
                    <span className="font-medium">{c}</span>
                    <span className="text-ink-faint"> · {pct.toFixed(0)}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      <motion.div variants={fadeUp} className="mt-6 card">
        <div className="text-xs text-ink-faint uppercase tracking-[0.14em] mb-2">editing</div>
        <h3 className="font-serif text-xl mb-3">How to change a personality prompt</h3>
        <ol className="text-sm text-ink-soft space-y-2 leading-relaxed list-decimal list-inside">
          <li>Edit <code className="text-xs px-1.5 py-0.5 bg-ink-card text-paper rounded">src/config.py</code> — PERSONAS and _PERSONA_INSTRUCTIONS dicts</li>
          <li>Run <code className="text-xs px-1.5 py-0.5 bg-ink-card text-paper rounded">sam deploy</code> to ship</li>
          <li>Users on that personality pick up the new behaviour on their next message</li>
        </ol>
        <p className="text-xs text-ink-faint mt-4">
          Runtime editing from this panel would need a new DynamoDB-backed config endpoint; editable here once the traffic justifies it.
        </p>
      </motion.div>
    </motion.div>
  );
}
